import React from "react";

/**
 * Reusable Alert Modal Component
 *
 * @param {boolean} isVisible - Controls modal visibility
 * @param {string} title - Modal title (default: "Alert")
 * @param {string} message - Modal message content
 * @param {string} icon - Emoji or icon to display (default: "⚠️")
 * @param {string} type - Modal type for styling: "warning", "error", "info", "success" (default: "warning")
 * @param {Array} buttons - Array of button configurations
 * @param {function} onClose - Function called when modal is closed (clicking backdrop)
 * @param {object} customStyles - Custom style overrides
 */
const AlertModal = ({
  isVisible = false,
  title = "Alert",
  message = "",
  icon = "⚠️",
  type = "warning",
  buttons = [],
  onClose = () => {},
  customStyles = {},
}) => {
  if (!isVisible) return null;

  // Default color schemes for different types
  const typeStyles = {
    warning: {
      iconColor: "#ff6b35",
      primaryButtonBg: "#ff6b35",
    },
    error: {
      iconColor: "#dc3545",
      primaryButtonBg: "#dc3545",
    },
    info: {
      iconColor: "#17a2b8",
      primaryButtonBg: "#17a2b8",
    },
    success: {
      iconColor: "#28a745",
      primaryButtonBg: "#28a745",
    },
  };

  const currentTypeStyle = typeStyles[type] || typeStyles.warning;

  // Default buttons if none provided
  const defaultButtons = [
    {
      text: "OK",
      onClick: onClose,
      variant: "primary",
    },
  ];

  const finalButtons = buttons.length > 0 ? buttons : defaultButtons;

  const getButtonStyle = (variant = "secondary") => {
    const baseStyle = {
      padding: "10px 20px",
      border: "none",
      borderRadius: "4px",
      cursor: "pointer",
      fontSize: "14px",
      fontWeight: variant === "primary" ? "bold" : "normal",
    };

    if (variant === "primary") {
      return {
        ...baseStyle,
        backgroundColor: currentTypeStyle.primaryButtonBg,
        color: "white",
      };
    }

    return {
      ...baseStyle,
      backgroundColor: "#6c757d",
      color: "white",
    };
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 2000,
        ...customStyles.overlay,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && onClose) {
          onClose();
        }
      }}
    >
      <div
        style={{
          backgroundColor: "white",
          borderRadius: "8px",
          padding: "20px",
          maxWidth: "500px",
          width: "90%",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)",
          ...customStyles.modal,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header with icon and title */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            marginBottom: "15px",
            ...customStyles.header,
          }}
        >
          <div
            style={{
              fontSize: "24px",
              color: currentTypeStyle.iconColor,
              marginRight: "10px",
              ...customStyles.icon,
            }}
          >
            {icon}
          </div>
          <h3
            style={{
              margin: 0,
              color: "#333",
              fontSize: "18px",
              fontWeight: "bold",
              ...customStyles.title,
            }}
          >
            {title}
          </h3>
        </div>

        {/* Message content */}
        <div
          style={{
            marginBottom: "20px",
            lineHeight: "1.5",
            color: "#555",
            whiteSpace: "pre-line",
            ...customStyles.message,
          }}
        >
          {message}
        </div>

        {/* Action buttons */}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "10px",
            ...customStyles.buttonContainer,
          }}
        >
          {finalButtons.map((button, index) => (
            <button
              key={index}
              onClick={button.onClick}
              style={{
                ...getButtonStyle(button.variant),
                ...button.customStyle,
              }}
              disabled={button.disabled}
            >
              {button.text}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AlertModal;

// Convenience hook for common alert patterns
export const useAlertModal = () => {
  const [modalConfig, setModalConfig] = React.useState({
    isVisible: false,
    title: "",
    message: "",
    type: "warning",
    buttons: [],
  });

  const showAlert = React.useCallback((config) => {
    setModalConfig({
      isVisible: true,
      ...config,
    });
  }, []);

  const hideAlert = React.useCallback(() => {
    setModalConfig((prev) => ({ ...prev, isVisible: false }));
  }, []);

  // Common alert patterns
  const showWarning = (message, title) => {
    return new Promise((resolve) => {
      setModalConfig({
        isVisible: true,
        title,
        message,
        buttons: [
          {
            text: "Cancel",
            onClick: () => {
              resolve(false); // User cancelled
              hideAlert();
            },
            variant: "secondary",
          },
          {
            text: "OK",
            onClick: () => {
              resolve(true); // User confirmed
              hideAlert();
            },
            variant: "primary",
          },
        ],
      });
    });
  };
  const showWarningx = React.useCallback(
    (message, title = "Warning", onConfirm, onCancel) => {
      const buttons = onConfirm
        ? [
            // { text: "Cancel", onClick: hideAlert, variant: "secondary" },
            {
              text: "Cancel",
              onClick: () => {
                if (onCancel) onCancel();
                hideAlert();
              },
              variant: "secondary",
            },
            {
              text: "Continue",
              onClick: () => {
                onConfirm();
                hideAlert();
              },
              variant: "primary",
            },
          ]
        : [{ text: "OK", onClick: hideAlert, variant: "primary" }];

      showAlert({
        title,
        message,
        type: "warning",
        buttons,
      });
    },
    [showAlert, hideAlert],
  );

  const showError = React.useCallback(
    (message, title = "Error") => {
      showAlert({
        title,
        message,
        type: "error",
        icon: "❌",
        buttons: [{ text: "OK", onClick: hideAlert, variant: "primary" }],
      });
    },
    [showAlert, hideAlert],
  );

  const showSuccess = React.useCallback(
    (message, title = "Success") => {
      showAlert({
        title,
        message,
        type: "success",
        icon: "✅",
        buttons: [{ text: "OK", onClick: hideAlert, variant: "primary" }],
      });
    },
    [showAlert, hideAlert],
  );

  const showInfo = React.useCallback(
    (message, title = "Information") => {
      showAlert({
        title,
        message,
        type: "info",
        icon: "ℹ️",
        buttons: [{ text: "OK", onClick: hideAlert, variant: "primary" }],
      });
    },
    [showAlert, hideAlert],
  );

  const showConfirm = React.useCallback(
    (message, title = "Confirm", onConfirm, onCancel) => {
      showAlert({
        title,
        message,
        type: "warning",
        icon: "❓",
        buttons: [
          {
            text: "Cancel",
            onClick: () => {
              if (onCancel) onCancel();
              hideAlert();
            },
            variant: "secondary",
          },
          {
            text: "Confirm",
            onClick: () => {
              if (onConfirm) onConfirm();
              hideAlert();
            },
            variant: "primary",
          },
        ],
      });
    },
    [showAlert, hideAlert],
  );

  return {
    modalConfig,
    showAlert,
    hideAlert,
    showWarning,
    showError,
    showSuccess,
    showInfo,
    showConfirm,
  };
};
